<script setup>
import MarketReadout from 'components/market/MarketReadout.vue';
import MarketSnapshot from 'components/market/MarketSnapshot.vue';
import MarketSpecialisationTable from 'components/market/MarketSpecialisationTable.vue';
import { usePageMeta } from 'composables/page-meta';
import {
    findMarketLocation,
    MARKET_LOCATIONS,
    resolveMarketLocationRedirect,
    TECH_MARKET_CLASSIFICATION_SLUG
} from 'src/configs/market';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { createPageNavigation } from 'src/utils/error-page';
import { buildPageMeta } from 'src/utils/page-meta-util';
import { trackProductEvent } from 'src/utils/umami-util';
import { tryBuildXprtJobsCategoryUrl } from 'src/utils/xprt-jobs-route-util';
import { useMarketStore } from 'stores/market-store';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

defineOptions({
    async preFetch({ currentRoute, redirect, ssrContext, store }) {
        const navigation = createPageNavigation({ currentRoute, redirect, ssrContext, store });
        const redirectSlug = resolveMarketLocationRedirect(currentRoute.params.locationSlug);
        if (redirectSlug) {
            return navigation.redirect(AppRoute.build(Route.MarketLocation, { locationSlug: redirectSlug }), 301);
        }
        const marketStore = useMarketStore();
        try {
            const market = await marketStore.loadLocation(currentRoute.params.locationSlug);
            // A superseded request returns undefined; only null means the requested market does not exist.
            if (market === null) {
                return navigation.raiseError(404);
            }
        } catch (err) {
            // Only a 404 means the page names nothing. Any other failure leaves a real page that cannot be served
            // right now, and 503 keeps its unavailable state out of search results without dropping the page.
            return navigation.raiseError(err?.response?.status === 404 ? 404 : 503);
        }
    }
});

const $route = useRoute();
const marketStore = useMarketStore();

const marketLocation = computed(() => findMarketLocation($route.params.locationSlug));

const locationSlug = computed(() => marketLocation.value?.slug || null);

const isKnownMarketLocation = computed(() => marketLocation.value !== null);

// Named from the configured set rather than from the response, so the heading always names the place that was asked
// for and can never drift to whatever the server resolved.
const locationName = computed(() => marketLocation.value?.name || 'Market location unavailable');

const canonicalUrl = computed(() => `https://xprt.dev/market/in/${locationSlug.value}`);

const isMarketAvailable = computed(() => marketStore.location !== null && marketStore.locationError === null);

const otherLocations = computed(() => MARKET_LOCATIONS.filter(location => location.slug !== locationSlug.value));

const jobsUrl = computed(() => {
    return locationSlug.value
        ? tryBuildXprtJobsCategoryUrl(TECH_MARKET_CLASSIFICATION_SLUG, locationSlug.value)
        : null;
});

const marketDescription = computed(() => {
    return `Explore tech job demand, advertised salaries, hiring employers and ICT specialisations across ${locationName.value}, `
        + 'based on active Xprt Jobs listings.';
});

usePageMeta(() => buildPageMeta({
    title: isKnownMarketLocation.value
        ? `${locationName.value} Tech Job Market`
        : 'Market location unavailable',
    description: isKnownMarketLocation.value
        ? marketDescription.value
        : 'Australian ICT market details for this location are currently unavailable.',
    canonicalUrl: isKnownMarketLocation.value ? canonicalUrl.value : null,
    robots: isKnownMarketLocation.value ? 'index, follow' : 'noindex,nofollow'
}));

function trackJobsHandoff() {
    if (!locationSlug.value) {
        return;
    }
    trackProductEvent('xprt_jobs_handoff', {
        component: 'market_location',
        classificationSlug: TECH_MARKET_CLASSIFICATION_SLUG,
        locationSlug: locationSlug.value
    });
}
</script>

<template>
    <main class="market-location-page">
        <header class="market-hero">
            <div class="container-md q-px-md q-py-xl">
                <nav aria-label="Breadcrumb" class="text-body2 q-mb-lg">
                    <a class="text-weight-medium" href="/market">Australian ICT market</a>
                    <q-icon aria-hidden="true" class="q-mx-xs" name="sym_r_chevron_right"/>
                    <span aria-current="page" class="text-hint">{{ locationName }}</span>
                </nav>
                <div class="hero-grid">
                    <div class="hero-copy">
                        <p class="font-mono text-caption text-uppercase text-hint q-mb-md">
                            Australian ICT market · Active listings
                        </p>
                        <h1 class="text-h3 text-sm-h2 text-md-h1 text-weight-bold q-mt-none q-mb-md">{{ locationName }}</h1>
                        <p class="hero-deck text-h6 text-weight-regular text-hint q-mt-none q-mb-lg">
                            Active roles, advertised pay and the organisations hiring across information and
                            communication technology in {{ locationName }}.
                        </p>
                        <q-btn
                            v-if="jobsUrl"
                            :href="jobsUrl"
                            color="primary"
                            data-test="location-jobs-link"
                            icon-right="sym_r_north_east"
                            label="View jobs on Xprt Jobs"
                            no-caps
                            rel="noopener noreferrer"
                            target="_blank"
                            unelevated
                            @click="trackJobsHandoff"
                        />
                    </div>
                    <MarketReadout :snapshot="marketStore.location"/>
                </div>
            </div>
        </header>
        <div class="container-md q-px-md q-py-xl">
            <q-banner
                v-if="marketStore.locationError && !marketStore.location"
                class="unavailable-banner q-mb-xl"
                role="status"
            >
                <template #avatar>
                    <q-icon color="primary" name="sym_r_cloud_off"/>
                </template>
                <strong>This location snapshot is currently unavailable.</strong>
                <div class="text-body2 q-mt-xs">No broader Australian result has been substituted for it.</div>
            </q-banner>
            <MarketSnapshot :is-available="isMarketAvailable" :location-name="locationName" :snapshot="marketStore.location" hide-readout/>
            <q-separator class="q-my-xl"/>
            <MarketSpecialisationTable
                :is-available="isMarketAvailable"
                :location-slug="locationSlug || undefined"
                :rows="marketStore.location?.subclassifications || []"
                :title="`Specialisations in ${locationName}`"
            />
            <p class="text-caption text-hint q-mt-md q-mb-none">
                The figures in this table are for {{ locationName }}. Each specialisation's own page describes all of
                Australia.
            </p>
            <q-separator class="q-my-xl"/>
            <nav aria-labelledby="other-locations-heading">
                <h2 id="other-locations-heading" class="text-h4 text-weight-bold q-mt-none q-mb-md">
                    Other states and territories
                </h2>
                <ul class="location-list q-pl-none q-my-none">
                    <li v-for="location of otherLocations" :key="location.slug" class="q-py-sm">
                        <a :href="`/market/in/${location.slug}`" class="text-weight-medium">{{ location.name }}</a>
                    </li>
                </ul>
            </nav>
            <q-banner class="method-note q-pa-md q-mt-xl" role="note">
                <template #avatar>
                    <q-icon color="primary" name="sym_r_info"/>
                </template>
                <strong>This page measures the whole state or territory</strong>
                <div class="text-body2 text-hint q-mt-xs">
                    A listing advertised for a suburb or a city is counted against the state it sits in, because that is
                    the level the underlying listings resolve to. These figures describe {{ locationName }} as a whole,
                    not any one city within it. The 30-day movement is calculated for Australia rather than per state,
                    so it is left out here rather than shown as if it were specific.
                </div>
                <template #action>
                    <q-btn color="primary" flat href="/methodology#note-1" label="Read note 1" no-caps/>
                </template>
            </q-banner>
        </div>
    </main>
</template>

<style lang="scss" scoped>
.market-location-page {
    .hero-grid {
        display: grid;
        gap: 48px;
        grid-template-columns: 1fr;
    }

    .hero-copy {
        max-width: 760px;
    }

    .hero-deck {
        max-width: 640px;
    }

    .location-list {
        columns: 2;
        list-style: none;

        @media (max-width: $breakpoint-xs-max) {
            columns: 1;
        }
    }

    .unavailable-banner,
    .method-note {
        background: rgba($primary, 0.06);
        border: 1px solid rgba($primary, 0.16);
    }
}
</style>
